import { useMemo } from "react";
import type { WordDTO } from "@shared/types";
import type { ChallengeKind } from "@shared/types";
import { AudioToVietnamese } from "./AudioToVietnamese";
import { AudioToEnglish } from "./AudioToEnglish";
import { VietnameseToEnglish } from "./VietnameseToEnglish";
import { EnglishToVietnamese } from "./EnglishToVietnamese";
import { SentenceGap } from "./SentenceGap";
import { AudioToImage } from "./AudioToImage";
import { ImageToWord } from "./ImageToWord";
import { ImageToAudio } from "./ImageToAudio";
import { WordToImage } from "./WordToImage";

interface Props {
  word: WordDTO;
  distractors: WordDTO[];
  onAnswer: (correct: boolean) => void;
  // Optional fixed kind (for forced re-answer in missed-word review).
  forceKind?: ChallengeKind;
  // Used to drive deterministic challenge-kind rotation per word in a run.
  rotationSeed?: number;
  /** Restrict to these kinds for this lesson. Null/empty = full rotation. */
  allowedKinds?: ChallengeKind[] | null;
}

// Full rotation when images and sentences are available.
const FULL_ROTATION: ChallengeKind[] = [
  "audio-to-image",
  "image-to-audio",
  "image-to-word",
  "audio-to-english",
  "word-to-image",
  "sentence-gap",
];

export function ChallengeRouter({
  word,
  distractors,
  onAnswer,
  forceKind,
  rotationSeed = 0,
  allowedKinds,
}: Props) {
  const kind = useMemo<ChallengeKind>(() => {
    if (forceKind) return forceKind;
    const baseRotation = allowedKinds && allowedKinds.length > 0 ? allowedKinds : FULL_ROTATION;
    // Filter to challenges this word actually supports.
    const distractorsHaveImages = distractors.slice(0, 3).every((d) => d.imageUrl);
    const haveImage = !!word.imageUrl && distractorsHaveImages;
    const haveAudio = distractors.slice(0, 3).every((d) => d.audioUrl) && !!word.audioUrl;
    const haveSentence = !!word.sentence?.toLowerCase().includes(word.english.toLowerCase());
    const supported = baseRotation.filter((k) => {
      if (!haveImage && (k === "audio-to-image" || k === "image-to-word" || k === "image-to-audio" || k === "word-to-image")) return false;
      if (!haveAudio && k === "image-to-audio") return false;
      if (!haveSentence && k === "sentence-gap") return false;
      return true;
    });
    if (supported.length === 0) return "audio-to-english"; // English-only fallback
    const idx = Math.abs(hash(word.id) + rotationSeed) % supported.length;
    return supported[idx]!;
  }, [word, distractors, forceKind, rotationSeed, allowedKinds]);

  switch (kind) {
    case "audio-to-image":
      return <AudioToImage word={word} distractors={distractors} onAnswer={onAnswer} />;
    case "image-to-audio":
      return <ImageToAudio word={word} distractors={distractors} onAnswer={onAnswer} />;
    case "image-to-word":
      return <ImageToWord word={word} distractors={distractors} onAnswer={onAnswer} />;
    case "audio-to-english":
      return <AudioToEnglish word={word} distractors={distractors} onAnswer={onAnswer} />;
    case "word-to-image":
      return <WordToImage word={word} distractors={distractors} onAnswer={onAnswer} />;
    case "audio-to-vi":
      return <AudioToVietnamese word={word} distractors={distractors} onAnswer={onAnswer} />;
    case "vi-to-en":
      return <VietnameseToEnglish word={word} distractors={distractors} onAnswer={onAnswer} />;
    case "en-to-vi":
      return <EnglishToVietnamese word={word} distractors={distractors} onAnswer={onAnswer} />;
    case "sentence-gap":
      return <SentenceGap word={word} distractors={distractors} onAnswer={onAnswer} />;
    default:
      return <AudioToEnglish word={word} distractors={distractors} onAnswer={onAnswer} />;
  }
}

function hash(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return h;
}
